import React, { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  Paper,
  Avatar,
} from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AuthContext } from "../contexts/AuthContext";
import { signup } from "../api/axios";
import { toast } from "react-toastify"; // ✅ Import toast
import "react-toastify/dist/ReactToastify.css";

export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mobileNo, setMobileNo] = useState("");
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { login: contextLogin } = useContext(AuthContext);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePassword = (pw) =>
    /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@#]{8,}$/.test(pw);

  const handleSignup = async () => {
    const newErrors = {};
    if (!name) newErrors.name = "Name is required.";
    if (!email) newErrors.email = "Email is required.";
    else if (!validateEmail(email)) newErrors.email = "Invalid email.";
    if (!password) newErrors.password = "Password is required.";
    else if (!validatePassword(password))
      newErrors.password = "Min 8 chars & one number.";
    if (!mobileNo) newErrors.mobileNo = "WhatsApp mobile no is required.";

    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    try {
      const res = await signup({
        name,
        email,
        password,
        mobileNo,
      });

      // Do NOT auto-login the user — redirect to login page
      toast.success("Account created successfully. Please login.");
      navigate("/login", { replace: true });
    } catch (err) {
      const message =
        err.response?.data?.error || "Signup failed. Please try again.";
      setErrors({ general: message });
      toast.error(message);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #6E8EF8, #A5DCF8)",
        p: 2,
      }}
    >
      <Box sx={{ maxWidth: 400, width: "100%" }}>
        <Paper elevation={4} sx={{ p: 4, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Avatar sx={{ bgcolor: "primary.main", mb: 1 }}>
              <LockOutlined />
            </Avatar>
            <Typography variant="h6">Create Account</Typography>
          </Box>

          {errors.general && (
            <Typography
              variant="body2"
              color="error"
              align="center"
              sx={{ mb: 1 }}
            >
              {errors.general}
            </Typography>
          )}

          <TextField
            fullWidth
            label="Mobile Number"
            variant="outlined"
            margin="dense"
            name="mobileNo"
            value={mobileNo}
            onChange={(e) => setMobileNo(e.target.value)}
            error={!!errors.mobileNo}
            helperText={errors.mobileNo}
            required
          />

          <TextField
            fullWidth
            label="Full Name"
            variant="outlined"
            margin="dense"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
          />

          <TextField
            fullWidth
            label="Email"
            variant="outlined"
            margin="dense"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            required
          />

          <TextField
            fullWidth
            label="Password"
            variant="outlined"
            margin="normal"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            variant="contained"
            size="medium"
            sx={{ mt: 2, py: 1 }}
            onClick={handleSignup}
          >
            Sign Up
          </Button>

          <Box textAlign="center" mt={1}>
            <Typography variant="body2">
              Already have an account?{" "}
              <Link to="/login" style={{ textDecoration: "none" }}>
                <Typography component="span" color="primary">
                  Login
                </Typography>
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
